import React, { useContext, useMemo } from 'react';
import { Card, CardContent, Grid, IconButton } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import { genTimeframeText, MARKETPLACE_TIERS } from '@onward-delivery/core';
import zipcode_to_timezone from 'zipcode-to-timezone';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { toNational } from '@/utilities/formatPhoneNumber';
import { colors } from '@/styles';
import {
    CONSIGNEELESS_ORDER_TYPES,
    CUSTOMERLESS_ORDER_TYPES,
    EDIT_ORDER_TABS,
    EDIT_ORDER_TABS_DISPLAY,
} from '../constants';
import { GreySubheader, Body, GreyBody, GrayBgCard } from '../../../styles/blocks';
import { filterAssignRatesForPinnedCarrier } from '../utilities/forwardStockingRates';
import { DROPOFF_TYPES } from '@/components/ShipmentForm/constants/dropoffOptions';
import dateFns from '@/utilities/dateFns';
import { asDateInTZ } from '@/utilities/convertToISO';
import { useSwappedAttributes } from '../hooks';
import { useClientUser } from '@/hooks';
import { carrierDisplayName } from '@/utilities/carrierAlias';
import { startCase } from 'lodash';
import { Option } from './Option';
import { MMOption } from './MMOption';
import { MarketplaceOption } from './MarketplaceOption';
import { Label, ErrorContainer } from './blocks';
import { getGroupedMarketplaceMeta } from '../utilities/pricingOptions';

const dateNumeric = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

const timeFormat = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
});

const GreyBox = styled.div`
    display: inline-block;
    background: #e4e4e4;
    border-radius: 4px;
    padding: 0.5rem;
    width: ${(props) => (props.fullWidth ? '100%' : '')};
    cursor: ${(props) => (props.pointer ? 'pointer' : '')};
`;

const Mask = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    display: block;
    z-index: 999;
`;

const SubCardTitle = styled((props) => <Body {...props} />)`
    font-weight: 600;
`;

const Icon = styled.div`
    width: 24px;
    height: 24px;
    margin-right: 16px;

    & svg {
        vertical-align: super;
    }
`;

function OrderCard({
    accordion = false,
    order,
    overridesById = {},
    marketplaceOverride,
    orderNumber,
    listing,
    validation,
    callbacks,
}) {
    const { location_type, location, zip, street, unit } = useSwappedAttributes(order);
    const isSaved = !!order.order_id;
    const isLoading = order.order_status === 'processing';
    const override_attr = order.oms ? 'onward_rate_override_id' : 'shipper_rate_override_id';
    const { service_levels, partnerCarriersById } = useClientUser();
    const isReturn = ['return', 'wh_return'].includes(order.order_type);
    const hasItems = order.itemsByOrderId?.length > 0;
    const hasServices = order.services?.length > 0;
    const contentLabel = hasItems && hasServices ? 'Items/Services' : hasServices ? 'Services' : 'Items';
    // Mixed (and items-only) default to the Items tab; services-only opens Services.
    const contentTab = hasServices && !hasItems ? EDIT_ORDER_TABS.SERVICES : EDIT_ORDER_TABS.ITEMS;

    const sorted = useMemo(() => {
        const assigned = (order?.pricing_options || []).filter((option) => {
            return !!overridesById[option?._meta?.override_id]?.assign;
        });
        const clone = filterAssignRatesForPinnedCarrier(assigned, overridesById, order);
        return clone.sort((l, r) => {
            const lc =
                overridesById[l?._meta?.override_id]?.partner_client ||
                overridesById[l?._meta?.override_id]?.assign ||
                {};
            const rc =
                overridesById[r?._meta?.override_id]?.partner_client ||
                overridesById[r?._meta?.override_id]?.assign ||
                {};

            return (rc?.business_name || 'Onward').localeCompare(lc?.business_name || 'Onward');
        });
    }, [order.pricing_options, overridesById, order.forward_stocked_status, order.carrier_id]);

    const marketplace = useMemo(() => {
        if (!marketplaceOverride?.pricing_override_id) {
            return null;
        }
        const options = (order?.pricing_options || []).filter(
            (option) => !overridesById[option?._meta?.override_id]?.assign
        );
        if (options.length === 0) {
            return {
                _meta: {
                    override_id: marketplaceOverride.pricing_override_id,
                },
                max_charge: null,
                min_charge: null,
            };
        }
        if (options.length === 1) {
            return {
                ...options[0],
                max_charge: options[0].total_charge,
                min_charge: options[0].total_charge,
            };
        }

        const prices = options.map((option) => option.total_charge);

        return {
            _meta: getGroupedMarketplaceMeta(options),
            max_charge: Math.max(...prices),
            min_charge: Math.min(...prices),
        };
    }, [overridesById, order.pricing_options, marketplaceOverride]);

    // The no-assign "marketplace" bucket keeps the generic Onward Marketplace
    // badge only for T3 fully-managed; other tiers brand the row with the
    // order's assigned carrier.
    const isFullyManagedMarketplace = marketplaceOverride?.marketplace_tier === MARKETPLACE_TIERS.T3_FULLY_MANAGED;

    // Both loaded-ness guards matter: JobDetails polls, and `overridesById` comes from
    // a different prop than `pricing_options`. On a tick where options have arrived but
    // overrides have not, every `assign` lookup is undefined and `sorted` empties — this
    // message would flash on a correctly-configured order.
    const noRatesForPinnedCarrier =
        !!order.forward_stocked_status &&
        !!order.carrier_id &&
        (order.pricing_options || []).length > 0 &&
        Object.keys(overridesById).length > 0 &&
        sorted.length === 0 &&
        !marketplace;

    const isInternal = !!order.oms;

    const hasValidationError = !!validation?.hasError?.();
    const missingTabs = validation?.missingTabs?.() || [];
    const missingIssues = validation?.missingIssues?.() || [];
    const hasMissingDetails = missingTabs.length > 0 || missingIssues.length > 0;
    const pricingWarnings = validation?.warnings?.() || [];

    const renderErrorIcon = (
        <Icon
            css={css`
                color: #d23e3e;
            `}
        >
            <ErrorIcon />
        </Icon>
    );
    const renderSuccessIcon = (
        <Icon
            css={css`
                color: ${colors.greens.primary};
            `}
        >
            <CheckCircleIcon />
        </Icon>
    );

    let icon;
    if (isSaved) {
        switch (order.order_status) {
            case 'processing':
                icon = null;
                break;
            case 'pending_confirmation':
                icon = hasValidationError ? renderErrorIcon : renderSuccessIcon;
                break;
            case 'incomplete':
                icon = renderErrorIcon;
                break;
            default:
                icon = hasValidationError ? renderErrorIcon : renderSuccessIcon;
                break;
        }
    } else if (hasValidationError) {
        // Unsaved orders previously skipped the icon switch entirely, so a freshly
        // imported order with a missing required field looked indistinguishable from
        // a healthy one. Surface the same red icon here so the disabled
        // "Price & Review" button is no longer mysterious.
        icon = renderErrorIcon;
    }

    const items = useMemo(() => {
        return order?.itemsByOrderId?.filter((item) => !item.is_return);
    }, [order]);
    const returns = useMemo(() => {
        return order?.itemsByOrderId?.filter((item) => item.is_return);
    }, [order]);
    const orderTZ = useMemo(() => {
        return (order[zip] && zipcode_to_timezone.lookup(order[zip])) || 'America/New_York';
    }, [order, zip]);

    const serviceLevel = useMemo(() => {
        return service_levels.find((s) => s.service_level_id === order.service_level_id);
    }, [order, service_levels]);

    const getLabel = (type, value, pickupOrDropoff) => {
        switch (type) {
            case 'Residence':
                return (
                    DROPOFF_TYPES[type].find((dropoff) => dropoff.value === value)?.label(pickupOrDropoff) ||
                    value ||
                    ''
                );
            case 'Business':
                let label =
                    DROPOFF_TYPES[type].find((dropoff) => dropoff.value === value || dropoff.label === value)?.label ||
                    value ||
                    '';
                return typeof label === 'function' ? label(pickupOrDropoff) : label;
            default:
                return value;
        }
    };

    // Scope the Delivery Type sub-card error to its own fields (Location Type,
    // Service Type). The CUSTOMER tab bucket also covers address/contact fields,
    // so reusing the tab-level flag would paint this box red for unrelated
    // address-only issues (e.g. a missing dropoff_city from a CDP geocode miss).
    const deliveryTypeHasError = useMemo(() => {
        if (order.order_type === 'will_call') return false;
        const locationTypeMissing = !order[location];
        const serviceTypeMissing =
            !serviceLevel?.service_level &&
            !getLabel(order[location], order[location_type], isReturn ? 'pickUp' : 'dropOff');
        return locationTypeMissing || serviceTypeMissing;
    }, [order, location, location_type, serviceLevel, isReturn]);

    const orderLabel = useMemo(() => {
        switch (order.order_type) {
            case 'will_call':
                return 'Will Call';
            case 'wh_inventory':
                return 'Inventory';
            case 'service':
                return 'Service Callback';
            default:
                return startCase(order.order_type || 'delivery');
        }
    }, [order.order_type]);

    const content = (
        <>
            <Grid
                container
                css={css`
                    margin-bottom: 1rem;
                `}
            >
                <Grid
                    container
                    item
                    xs={6}
                    css={css`
                        align-items: center;
                    `}
                >
                    <GreySubheader
                        css={css`
                            margin: 0;
                        `}
                    >
                        {`Order ${order.order_number || orderNumber || ''} - ${orderLabel}`}
                    </GreySubheader>
                </Grid>
                <Grid
                    container
                    item
                    xs={6}
                    css={css`
                        align-items: center;
                        justify-content: flex-end;
                    `}
                >
                    {icon}
                    {order.oms || order.source_form !== 'APIv2' ? (
                        <IconButton
                            css={css`
                                padding: 6px;
                            `}
                        >
                            <DeleteOutlinedIcon
                                onClick={() => {
                                    callbacks.setDeleteOrderModal(order);
                                }}
                                css={css`
                                    color: ${colors.reds.primary};
                                `}
                            />
                        </IconButton>
                    ) : null}
                </Grid>
            </Grid>

            {hasMissingDetails ? (
                <Grid
                    container
                    alignItems="center"
                    css={css`
                        background: #fdecea;
                        border: 1px solid #d23e3e;
                        border-radius: 4px;
                        padding: 8px 12px;
                        margin-bottom: 1rem;
                        column-gap: 8px;
                        row-gap: 4px;
                    `}
                >
                    <ErrorIcon
                        css={css`
                            color: #d23e3e;
                            font-size: 18px;
                        `}
                    />
                    <Body
                        css={css`
                            color: #d23e3e;
                            font-weight: 600;
                        `}
                    >
                        {missingTabs.length > 0 ? 'Missing required fields. Click to fix:' : 'Needs attention:'}
                    </Body>
                    {missingTabs.map((tab) => (
                        <Body
                            key={tab}
                            onClick={() => callbacks.openEditModal(order, tab)}
                            css={css`
                                color: #d23e3e;
                                text-decoration: underline;
                                cursor: pointer;
                                font-weight: 600;
                            `}
                        >
                            {EDIT_ORDER_TABS_DISPLAY[tab] || tab}
                        </Body>
                    ))}
                    {missingIssues.map((issue) => (
                        <Body
                            key={issue}
                            css={css`
                                flex-basis: 100%;
                                color: #d23e3e;
                                font-weight: 600;
                            `}
                        >
                            {issue}
                        </Body>
                    ))}
                </Grid>
            ) : null}

            {pricingWarnings.length > 0 ? (
                <Grid
                    container
                    alignItems="center"
                    css={css`
                        background: ${colors.golds.highlight};
                        border: 1px solid ${colors.golds.primary};
                        border-radius: 4px;
                        padding: 8px 12px;
                        margin-bottom: 1rem;
                        column-gap: 8px;
                        row-gap: 4px;
                    `}
                >
                    <WarningIcon
                        css={css`
                            color: ${colors.golds.primary};
                            font-size: 18px;
                        `}
                    />
                    {pricingWarnings.map((warning) => (
                        <Body
                            key={warning}
                            css={css`
                                font-weight: 600;
                            `}
                        >
                            {warning}
                        </Body>
                    ))}
                </Grid>
            ) : null}

            <Grid
                container
                spacing={2}
                css={css`
                    margin-bottom: 1rem;
                `}
            >
                {!CUSTOMERLESS_ORDER_TYPES.includes(order.order_type) && (
                    <Grid item xs={3}>
                        <Grid item xs={12}>
                            <SubCardTitle>Customer</SubCardTitle>
                        </Grid>
                        <GrayBgCard
                            pointer="true"
                            onClick={() => !callbacks.openEditModal(order, EDIT_ORDER_TABS.CUSTOMER)}
                            validationError={validation.hasErrorTab(EDIT_ORDER_TABS.CUSTOMER)}
                        >
                            {/* Contacts are positional: name/phone stay on dropoff_* even for
                            returns; only the address family swaps to pickup_*. */}
                            {order.dropoff_name && <Body>{order.dropoff_name}</Body>}
                            {order[street] && (
                                <Body>
                                    {order[street]}
                                    {(() => {
                                        const unitValue = order[unit];
                                        if (!unitValue) return '';
                                        // Prefix a bare-integer unit with "#" ("200" → " #200");
                                        // labeled units ("Apt 5", "Suite 200") pass through as-is.
                                        return /^\d+$/.test(String(unitValue).trim())
                                            ? ` #${unitValue}`
                                            : ` ${unitValue}`;
                                    })()}
                                </Body>
                            )}
                            {order.dropoff_phone ? <Body>{toNational(order.dropoff_phone)}</Body> : null}
                        </GrayBgCard>
                    </Grid>
                )}
                {!CUSTOMERLESS_ORDER_TYPES.includes(order.order_type) && (
                    <Grid item xs={3}>
                        <Grid item xs={12}>
                            <SubCardTitle>Delivery Type</SubCardTitle>
                        </Grid>
                        <GrayBgCard
                            fullWidth
                            pointer="true"
                            onClick={() => callbacks.openEditModal(order, EDIT_ORDER_TABS.CUSTOMER, -500)}
                            validationError={deliveryTypeHasError}
                        >
                            <Body>Location Type: {startCase(order[location]) || 'TBD'}</Body>
                            <Body>
                                Service Type:{' '}
                                {serviceLevel?.service_level
                                    ? serviceLevel.service_level
                                    : getLabel(
                                          order[location],
                                          order[location_type],
                                          isReturn ? 'pickUp' : 'dropOff'
                                      ) || 'TBD'}
                            </Body>
                        </GrayBgCard>
                    </Grid>
                )}
                <Grid item xs={3}>
                    <Grid item xs={12}>
                        <SubCardTitle>{contentLabel}</SubCardTitle>
                    </Grid>
                    <GrayBgCard
                        fullWidth
                        pointer="true"
                        onClick={() => callbacks.openEditModal(order, contentTab)}
                        validationError={
                            validation.hasErrorTab(EDIT_ORDER_TABS.ITEMS) ||
                            validation.hasErrorTab(EDIT_ORDER_TABS.SERVICES)
                        }
                    >
                        {hasItems || hasServices ? (
                            <>
                                {!!items?.length && (
                                    <Body>
                                        {(() => {
                                            const count = items.reduce((acc, item) => acc + item.quantity, 0);
                                            return `${count} ${count === 1 ? 'item' : 'items'}`;
                                        })()}
                                    </Body>
                                )}
                                {!!returns?.length && (
                                    <Body>
                                        {(() => {
                                            const count = returns.reduce((acc, item) => acc + item.quantity, 0);
                                            return `${count} ${count === 1 ? 'return' : 'returns'}`;
                                        })()}
                                    </Body>
                                )}
                                {hasServices && (
                                    <Body>
                                        {`${order.services.length} ${
                                            order.services.length === 1 ? 'service' : 'services'
                                        }`}
                                    </Body>
                                )}
                            </>
                        ) : (
                            <Body>None</Body>
                        )}
                    </GrayBgCard>
                </Grid>
                {!CONSIGNEELESS_ORDER_TYPES.includes(order.order_type) && (
                    <Grid item xs={3}>
                        <Grid item xs={12}>
                            <SubCardTitle>Add haul-away</SubCardTitle>
                        </Grid>
                        <GrayBgCard
                            pointer
                            onClick={() => callbacks.openEditModal(order, EDIT_ORDER_TABS.HAUL_AWAY)}
                            css={css`
                                justify-content: center;
                            `}
                        >
                            <Body>
                                {order?.haulaway_items?.length ? order?.haulaway_items?.length : 'None'}
                                {order?.haulaway_items?.length > 0 ? ` Item(s)` : ''}
                            </Body>
                            <GreyBody>(Optional)</GreyBody>
                        </GrayBgCard>
                    </Grid>
                )}
            </Grid>
            <Grid
                container
                spacing={2}
                css={css`
                    margin-bottom: 24px;
                `}
            >
                <Grid
                    container
                    item
                    xs={3}
                    css={css`
                        align-items: center;
                    `}
                >
                    <SubCardTitle
                        css={css`
                            margin: 0;
                            margin-right: 12px;
                        `}
                    >
                        Delivery Date{' '}
                    </SubCardTitle>
                    <GreyBox>
                        <GreyBody>
                            {dateFns.formatDate(asDateInTZ(order.delivery_date, orderTZ), 'yyyy-MM-dd') || 'TBD'}
                        </GreyBody>
                    </GreyBox>
                </Grid>
                <Grid
                    container
                    item
                    xs={3}
                    css={css`
                        align-items: center;
                    `}
                >
                    <SubCardTitle
                        css={css`
                            margin: 0;
                            margin-right: 12px;
                        `}
                    >
                        PO #
                    </SubCardTitle>
                    <GrayBgCard
                        pointer="true"
                        onClick={() => callbacks.openEditModal(order, EDIT_ORDER_TABS.ORDER_DETAILS)}
                        validationError={validation.hasErrorTab(EDIT_ORDER_TABS.ORDER_DETAILS)}
                        css={css`
                            padding: 8px;
                            height: auto;
                        `}
                    >
                        <GreyBody
                            css={css`
                                margin: 0;
                            `}
                        >
                            {order.po_number ? order.po_number : 'TBD'}
                        </GreyBody>
                    </GrayBgCard>
                </Grid>
                <Grid item conatiner xs={3} />
                {order.oms ? (
                    <Grid
                        container
                        item
                        xs={3}
                        css={css`
                            align-items: center;
                        `}
                    >
                        <SubCardTitle
                            css={css`
                                margin: 0;
                                margin-right: 12px;
                            `}
                        >
                            Revenue
                        </SubCardTitle>
                        <GrayBgCard
                            pointer="true"
                            onClick={() => callbacks.openEditModal(order, EDIT_ORDER_TABS.ORDER_DETAILS)}
                            validationError={validation.hasErrorTab(EDIT_ORDER_TABS.ORDER_DETAILS)}
                            css={css`
                                padding: 8px;
                                height: auto;
                            `}
                        >
                            <GreyBody
                                css={css`
                                    margin: 0;
                                `}
                            >
                                {order.order_revenue ? `$${order.order_revenue.toFixed(2)}` : '-'}
                            </GreyBody>
                        </GrayBgCard>
                    </Grid>
                ) : null}
            </Grid>
            {isSaved && !order.oms && order?.pricing_options?.length > 0 ? (
                <Grid container direction="row">
                    <Grid
                        container
                        direction="column"
                        css={css`
                            width: 100%;
                        `}
                    >
                        {!order[override_attr] ? (
                            <Grid
                                container
                                direction="row"
                                css={css`
                                    margin-bottom: 8px;
                                    align-items: center;
                                `}
                            >
                                <Label
                                    css={css`
                                        color: #d23e3e;
                                    `}
                                >
                                    {noRatesForPinnedCarrier
                                        ? null
                                        : sorted.length > 0
                                        ? 'Must select carrier'
                                        : 'We couldnt calculate a rate for this order'}
                                </Label>

                                {order?.last_pricing_run?.status === 'COMPLETED' ? (
                                    <IconButton
                                        disabled={(!order.last_pricing_run?.instances || []).some(
                                            (instance) =>
                                                instance?.middle_mile?.status === 'FAILED' ||
                                                instance?.final_mile?.status === 'FAILED'
                                        )}
                                        css={css`
                                            padding: 6px;
                                        `}
                                    >
                                        <HelpIcon
                                            onClick={() => {
                                                callbacks.setDiagnostic({ order });
                                            }}
                                            css={css`
                                                color: ${colors.greys.primary};
                                            `}
                                        />
                                    </IconButton>
                                ) : null}
                            </Grid>
                        ) : null}

                        <Grid container direction="row">
                            <ErrorContainer error={!order[override_attr]}>
                                {noRatesForPinnedCarrier ? (
                                    <Body>
                                        No rates are configured for{' '}
                                        {carrierDisplayName(order.order_carrier, partnerCarriersById) || 'this carrier'}{' '}
                                        on forward-stocked orders. Contact support to have this carrier&apos;s pricing
                                        set up.
                                    </Body>
                                ) : null}
                                {marketplace ? (
                                    <MarketplaceOption
                                        tz={orderTZ}
                                        override_attr={override_attr}
                                        breakdown={marketplace}
                                        overridesById={overridesById}
                                        order={order}
                                        callbacks={callbacks}
                                        client={isFullyManagedMarketplace ? null : order.order_carrier}
                                        subtext={
                                            order.has_middle_mile_origin
                                                ? 'Middle Mile + Final Mile'
                                                : genTimeframeText({ order, breakdown: marketplace, tz: orderTZ })
                                        }
                                    />
                                ) : null}
                                {sorted.map((breakdown) => {
                                    return !order.has_middle_mile_origin ||
                                        breakdown?._meta?.override_type === 'E2E' ? (
                                        <Option
                                            tz={orderTZ}
                                            override_attr={override_attr}
                                            breakdown={breakdown}
                                            overridesById={overridesById}
                                            order={order}
                                            callbacks={callbacks}
                                            subtext={
                                                order.has_middle_mile_origin
                                                    ? 'End to End'
                                                    : genTimeframeText({ order, breakdown, tz: orderTZ })
                                            }
                                        />
                                    ) : (
                                        <MMOption
                                            tz={orderTZ}
                                            override_attr={override_attr}
                                            breakdown={breakdown}
                                            overridesById={overridesById}
                                            order={order}
                                            callbacks={callbacks}
                                        />
                                    );
                                })}
                            </ErrorContainer>
                        </Grid>
                    </Grid>
                </Grid>
            ) : null}
        </>
    );

    return accordion ? (
        content
    ) : (
        <Card
            css={css`
                position: relative;
                margin-bottom: 1rem;
                opacity: ${isLoading ? 0.3 : 1};
            `}
        >
            {isLoading ? <Mask /> : null}
            <CardContent>{content}</CardContent>
        </Card>
    );
}

// React.memo (shallow compare): OrderCard is the leaf of the order-list render
// path and re-renders on every Context update. Safe only because call sites
// pass referentially-stable props — an inline-constructed prop defeats the memo.
export default React.memo(OrderCard);
